import { Component, effect, Input, OnInit } from '@angular/core';
import { Filter } from '../models/filter-models';
import { FormsModule } from '@angular/forms';
import { Tier } from '../models/item-data-models';
import { FilterService } from '../services/filter.service';
import { CurrencyTiersComponent } from './currency-tiers/currency-tiers.component';
import { GearTiersComponent } from './gear-tiers/gear-tiers.component';
import { JewelleryTiersComponent } from './jewellery-tiers/jewellery-tiers.component';
import { UniqueTiersComponent } from './unique-tiers/unique-tiers.component';
import { ChanceBaseTiersComponent } from './chance-base-tiers/chance-base-tiers.component';
import { ModTiersComponent } from './mod-tiers/mod-tiers.component';
import { EquipmentTiersComponent } from './equipment-tiers/equipment-tiers.component';
import { EconomyService } from '../services/economy.service';
import { ResponsiveService } from '../services/responsive.service';
import { ValueComponent } from '../common/value/value.component';
import { BaseComponent } from '../common/base.component';
import { Economy, InflationData } from '../models/economy-models';
import { SelectComponent, SelectOptionComponent } from '../common/select/select.component';
import { NavigationService } from '../services/navigation.service';

@Component({
  selector: 'app-tier-list-tab',
  templateUrl: './tier-list-tab.component.html',
  styleUrls: ['./tier-tab-common.scss', './tier-list-tab.component.scss'],
  imports: [FormsModule, CurrencyTiersComponent, GearTiersComponent, JewelleryTiersComponent, UniqueTiersComponent, ChanceBaseTiersComponent,
    ModTiersComponent, EquipmentTiersComponent, ValueComponent, SelectComponent, SelectOptionComponent]
})
export class TierListTabComponent extends BaseComponent implements OnInit {
  @Input({ required: true }) filter!: Filter;

  /** When true, each tier sub-tab lists only items with a custom tier (↻ reset visible). */
  showChangedTierItemsOnly = false;

  inflationData?: InflationData;

  Tier = Tier;
  Economy = Economy;

  tierListTab: tierListTabType = 'currency';

  tabNameResetTierMap: Record<tierListTabType, string> = {
    'currency': 'Currency',
    'divination-cards': 'Currency',
    'uniques': 'Unique',
    'chance-bases': 'Chance Base',
    'gear': 'Gear Base',
    'jewellery': 'Jewellery Base',
    'items': 'Item',
    'mods': 'Mod',
  };

  constructor(
    private filterService: FilterService,
    private economyService: EconomyService,
    private responsiveService: ResponsiveService,
    private navigationService: NavigationService,
  ) {
    super();
    this.inflationData = this.economyService.getInflationData();

    effect(() => {
      const subTab = this.navigationService.tierListSubTabTarget();
      if (!subTab) return;
      this.selectTab(subTab);
      this.navigationService.tierListSubTabTarget.set(null);
    });
  }

  ngOnInit(): void {
    // Ensure the correct tab is selected on initial page load
    const initialUrl = window.location.pathname.split('/')[2];
    this.tierListTab = initialUrl as any || 'currency';
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  resetCurrentTabCustomTiers(evt: Event) {
    if (!confirm(`This will reset all custom ${this.tabNameResetTierMap[this.tierListTab]} tiers. Are you sure?`)) {
      return;
    }

    evt.preventDefault();
    switch (this.tierListTab) {
      case 'currency': this.filterService.resetAllCurrencyItemTiers(); break;
      case 'uniques': this.filterService.resetAllUniqueItemTiers(); break;
      case 'chance-bases': this.filterService.resetAllChanceBaseItemTiers(); break;
      case 'gear': this.filterService.resetAllGearItemTiers(); break;
      case 'jewellery': this.filterService.resetAllJewelleryItemTiers(); break;
      case 'items': this.filterService.resetAllEquipmentItemTiers(); break;
      case 'mods': this.filterService.resetAllModTiers(); break;
    }
    this.refresh();
  }

  resetAllCustomTiers(evt: Event) {
    if (!confirm('This will reset all custom Currency, Unique, Chance Base, Gear, Jewellery, Item and Mod tiers. Custom Mod Categories will not be reset. Are you sure?')) {
      return;
    }

    evt.preventDefault();
    this.filterService.resetAllCurrencyItemTiers();
    this.filterService.resetAllGearItemTiers();
    this.filterService.resetAllJewelleryItemTiers();
    this.filterService.resetAllEquipmentItemTiers();
    this.filterService.resetAllUniqueItemTiers();
    this.filterService.resetAllChanceBaseItemTiers();
    this.filterService.resetAllModTiers();
    this.refresh();
  }

  updateEconomy() {
    this.filterService.updateAllEconomyTiers();
    this.filterService.updateFilter(true);
    this.refresh();
  }

  private refresh() {
    const currentTab = this.tierListTab;
    this.tierListTab = ('dummy' as any);
    setTimeout(() => this.tierListTab = currentTab, 0);
  }

  selectTab(tierListTab: tierListTabType) {
    this.tierListTab = tierListTab;

    // Update the URL without reloading the page
    window.history.replaceState({}, '', `/tier-list/${tierListTab}`);
  }
}

type tierListTabType = 'currency' | 'uniques' | 'chance-bases' | 'gear' | 'jewellery' | 'items' | 'divination-cards' | 'mods';
